import { useEffect, useReducer, useRef } from "react";
import { CONDITION_STRUCTURE } from "@builder/utils/constants";
import { v4 } from "uuid";

export interface ConditionType {
    _id: string;
    source: string;
    condition: string;
    target: string;
}

export interface ConditionState {
    conditions: ConditionType[][];
}

interface ConditionAction {
    type: "SELECT" | "ADD_CONDITION" | "ADD_GROUP" | "REMOVE_CONDITION" | "RESET";
    groupIndex?: number;
    conditionIndex?: number;
    key?: CONDITION_STRUCTURE;
    value?: string;
    resetValue?: ConditionState;
}

export const initialState: ConditionState = {
    conditions: [
        [
            {
                _id: v4(),
                source: "",
                condition: "",
                target: ""
            }
        ]
    ]
};

const reducer = (state: ConditionState, action: ConditionAction): ConditionState => {
    switch (action.type) {
        case "SELECT": {
            return {
                ...state,
                conditions: state.conditions.map((group, groupIndex) =>
                    groupIndex === action.groupIndex
                        ? group.map((condition, conditionIndex) =>
                              conditionIndex === action.conditionIndex ? { ...condition, [action.key as string]: action.value } : condition
                          )
                        : group
                )
            };
        }
        case "ADD_CONDITION": {
            return {
                ...state,
                conditions: state.conditions.map((group, groupIndex) =>
                    groupIndex === action.groupIndex ? [...group, { _id: v4(), source: "", condition: "", target: "" }] : group
                )
            };
        }
        case "ADD_GROUP": {
            return {
                ...state,
                conditions: [...state.conditions, [{ _id: v4(), source: "", condition: "", target: "" }]]
            };
        }
        case "REMOVE_CONDITION": {
            const isCurrentStateEmpty =
                state.conditions.length === 1 &&
                state.conditions[0].length === 1 &&
                state.conditions[0][0].source === "" &&
                state.conditions[0][0].condition === "" &&
                state.conditions[0][0].target === "";

            if (isCurrentStateEmpty) {
                return state;
            }

            const newConditions = state.conditions
                .map((group, groupIndex) =>
                    groupIndex === action.groupIndex ? group.filter((_, conditionIndex) => conditionIndex !== action.conditionIndex) : group
                )
                .filter(group => group.length > 0);

            const isLastCondition = newConditions.length === 0 || newConditions.every(group => group.length === 0);

            if (isLastCondition) {
                return {
                    ...state,
                    conditions: initialState.conditions
                };
            }
            return {
                ...state,
                conditions: newConditions
            };
        }

        case "RESET": {
            return action.resetValue || initialState;
        }

        default:
            throw new Error();
    }
};

const useConditions = (defaultValues?: ConditionState, currentNode?: string) => {
    const [state, dispatch] = useReducer(reducer, defaultValues || initialState);
    const currentNodeId = useRef<string>("");

    useEffect(() => {
        if (currentNodeId.current !== currentNode && currentNode) {
            currentNodeId.current = currentNode;
            dispatch({ type: "RESET", resetValue: defaultValues });
        }
    }, [defaultValues]);

    const handleSelect = (groupIndex: number, conditionIndex: number, key: CONDITION_STRUCTURE, value: string) => {
        dispatch({ type: "SELECT", groupIndex, conditionIndex, key, value });
    };

    const addCondition = (groupIndex: number) => {
        dispatch({ type: "ADD_CONDITION", groupIndex });
    };

    const addGroup = () => {
        dispatch({ type: "ADD_GROUP" });
    };

    const removeCondition = (groupIndex: number, conditionIndex: number) => {
        dispatch({ type: "REMOVE_CONDITION", groupIndex, conditionIndex });
    };

    const removeAllConditions = () => {
        dispatch({ type: "RESET" });
    };

    return { state, handleSelect, addCondition, addGroup, removeCondition, removeAllConditions };
};

export default useConditions;
